import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ClipboardList, ExternalLink, CheckCircle2, AlertCircle, AlertTriangle,
  Filter, X as XIcon, ListPlus, Shield, User as UserIcon, Clock, CalendarClock
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { ACTION_LABELS, describeChangeDetails } from '../utils/changeLabels';
import { matchesReviewAsset } from '../lib/reviewAssetSearch';
import { ReviewPostponeDialog } from '../components/ReviewPostponeDialog';
import { ReviewPostponementInfo } from '../components/ReviewPostponementInfo';
import ResolveReviewModal from '../components/ResolveReviewModal';
import MisTareasBlock from '../components/MisTareasBlock';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const TIPO_OP_LABELS = {
  CREDITO: 'Crédito',
  SUBASTA: 'Subasta',
  REMATE: 'Remate',
  COMPRA_DIRECTA: 'Compra Directa',
  OCUPADO: 'Ocupado',
  REO: 'REO',
};

const ROLE_LABELS = {
  superadmin: 'Superadmin',
  admin: 'Admin',
  gestor: 'Gestor',
  mediador: 'Mediador',
  inversor: 'Inversor',
};

const PRIORITY_COLORS = {
  alta: { bg: 'rgba(239, 68, 68, 0.15)', text: '#fca5a5', label: 'Alta' },
  media: { bg: 'rgba(251, 191, 36, 0.15)', text: '#fcd34d', label: 'Media' },
  baja: { bg: 'rgba(100, 116, 139, 0.18)', text: '#cbd5e1', label: 'Baja' },
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('es-ES', { day: '2-digit', month: 'short', year: '2-digit', hour: '2-digit', minute: '2-digit' });
  } catch { return iso; }
};

const fmtDateOnly = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso + (iso.length === 10 ? 'T00:00:00' : '')).toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return iso; }
};

// Colorea la fecha de vencimiento (due_date) para vista personal.
const dueColor = (dd, todayIso) => {
  if (!dd) return 'text-slate-500';
  if (dd < todayIso) return 'text-red-400 font-medium';
  return 'text-slate-300';
};

const PendientesRevisionPage = () => {
  const { user } = useAuth();
  const isInversor = user?.role === 'inversor';
  const isSuperadmin = user?.role === 'superadmin';
  const canSearchSupplierId = ['superadmin', 'admin', 'gestor'].includes(user?.role);

  // === Scope: Mis revisiones | Todas las revisiones (solo superadmin) ===
  const [scope, setScope] = useState('mine');

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const activeRequest = useRef(null);
  const [counts, setCounts] = useState({ pendiente: 0, revisado: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [postponeTarget, setPostponeTarget] = useState(null);
  const [resolveTarget, setResolveTarget] = useState(null);

  // Filtros comunes
  const [statusFilter, setStatusFilter] = useState('pendiente');   // pendiente | revisado | all
  const [priorityFilter, setPriorityFilter] = useState('');         // ''|'alta'|'media'|'baja'
  const [dueFilter, setDueFilter] = useState('');                   // ''|'vencido'|'proximos7'|'sin_fecha'
  const [sortBy, setSortBy] = useState('due_asc');
  const [assetSearch, setAssetSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const timeout = setTimeout(() => setDebouncedSearch(assetSearch.trim()), 250);
    return () => clearTimeout(timeout);
  }, [assetSearch]);
  const [assignedToMe, setAssignedToMe] = useState(false);

  // Filtros exclusivos de "Todas las revisiones"
  const [destinatarioFilter, setDestinatarioFilter] = useState('');
  const [solicitanteFilter, setSolicitanteFilter] = useState('');
  const [vencimientoFilter, setVencimientoFilter] = useState('todas'); // todas | vencidas | en_plazo

  // Catálogo de usuarios para poblar selects de "Todas las revisiones"
  const [supUsers, setSupUsers] = useState([]);

  const todayIso = useMemo(() => new Date().toISOString().slice(0, 10), []);

  // Al pasar a scope=all cargamos el catálogo (una vez)
  useEffect(() => {
    if (!isSuperadmin) return;
    if (scope !== 'all') return;
    if (supUsers.length > 0) return;
    axios.get(`${API}/me/pendientes-revision/users`, { withCredentials: true })
      .then(r => setSupUsers(r.data?.users || []))
      .catch(() => {});
  }, [isSuperadmin, scope, supUsers.length]);

  const fetchItems = useCallback(async () => {
    activeRequest.current?.abort();
    const controller = new AbortController();
    activeRequest.current = controller;
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      params.set('status', statusFilter);
      if (priorityFilter) params.set('priority', priorityFilter);
      if (dueFilter) params.set('due', dueFilter);
      params.set('sort', scope === 'all' ? 'due_asc' : sortBy);
      params.set('limit', '300');
      params.set('scope', scope);
      if (debouncedSearch) params.set('asset_search', debouncedSearch);
      if (scope === 'all') {
        if (destinatarioFilter) params.set('destinatario_id', destinatarioFilter);
        if (solicitanteFilter) params.set('solicitante_id', solicitanteFilter);
        if (vencimientoFilter && vencimientoFilter !== 'todas') params.set('vencimiento', vencimientoFilter);
      }
      const r = await axios.get(`${API}/me/pendientes-revision?${params.toString()}`, { withCredentials: true, signal: controller.signal });
      if (controller.signal.aborted) return;
      setItems(r.data?.items || []);
      setTotal(r.data?.total ?? r.data?.items?.length ?? 0);
      setCounts(r.data?.counts || { pendiente: 0, revisado: 0 });
    } catch (e) {
      if (!controller.signal.aborted) setError(e?.response?.data?.detail || 'Error al cargar los pendientes');
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  }, [statusFilter, priorityFilter, dueFilter, sortBy, scope, destinatarioFilter, solicitanteFilter, vencimientoFilter, debouncedSearch]);

  useEffect(() => { fetchItems(); return () => activeRequest.current?.abort(); }, [fetchItems]);

  // Client-side filtering (search + asignado a mí)
  const filteredItems = useMemo(() => {
    const q = assetSearch.trim().toLowerCase();
    return items.filter(it => {
      if (q) {
        if (!matchesReviewAsset(it, q)) return false;
      }
      if (assignedToMe && user?.id && it.user_id === user.id) return false;
      return true;
    });
  }, [items, assetSearch, assignedToMe, user]);

  const updatePriority = async (auditLogId, priority) => {
    try {
      await axios.patch(`${API}/me/pendientes-revision/${auditLogId}`, { priority }, { withCredentials: true });
      await fetchItems();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo actualizar prioridad');
    }
  };

  const updateDueDate = async (auditLogId, due_date) => {
    try {
      await axios.patch(`${API}/me/pendientes-revision/${auditLogId}`, { due_date }, { withCredentials: true });
      await fetchItems();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo actualizar fecha');
    }
  };

  const handleCrearTarea = (it) => {
    const payload = {
      pending_review_id: it.audit_log_id,
      asset_id: it.asset_id,
      suggested_task_title: ACTION_LABELS[it.action] || it.action,
      suggested_assigned_role: user?.role || null,
    };
    console.info('[crear-tarea suggestion]', payload);
    toast.info('Funcionalidad preparada para el módulo Tareas');
  };

  const clearFilters = () => {
    setStatusFilter('pendiente');
    setPriorityFilter('');
    setDueFilter('');
    setSortBy('due_asc');
    setAssetSearch('');
    setAssignedToMe(false);
    setDestinatarioFilter('');
    setSolicitanteFilter('');
    setVencimientoFilter('todas');
  };

  const hasActiveFilters = statusFilter !== 'pendiente' || priorityFilter || dueFilter || sortBy !== 'due_asc'
    || assetSearch || assignedToMe
    || (scope === 'all' && (destinatarioFilter || solicitanteFilter || (vencimientoFilter && vencimientoFilter !== 'todas')));

  const isAllScope = scope === 'all';

  return (
    <div className="space-y-6" data-testid="pendientes-revision-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2" style={{ color: 'var(--color-text-primary)' }}>
            <ClipboardList className="w-6 h-6" style={{ color: 'var(--color-blue-accent)' }} />
            Pendientes de revisión
          </h1>
          <p className="text-sm mt-1" style={{ color: 'var(--color-text-muted)' }}>
            {isAllScope
              ? 'Vista de supervisión: todas las revisiones activas de la aplicación.'
              : 'Cambios y novedades marcados para revisión más adelante.'}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div
            className="px-3 py-1.5 rounded-lg text-sm font-medium"
            style={{ background: 'rgba(239, 68, 68, 0.15)', color: '#fca5a5' }}
            data-testid="pendientes-count-pendientes"
          >
            {counts.pendiente} pendiente{counts.pendiente === 1 ? '' : 's'}
          </div>
          <div
            className="px-3 py-1.5 rounded-lg text-sm"
            style={{ background: 'rgba(16, 185, 129, 0.12)', color: '#6ee7b7' }}
            data-testid="pendientes-count-revisados"
          >
            {counts.revisado} resuelto{counts.revisado === 1 ? '' : 's'}
          </div>
        </div>
      </div>

      <MisTareasBlock canViewTeam={['admin', 'superadmin'].includes(user?.role)} readOnly={isInversor} />

      {/* Scope toggle — solo superadmin */}
      {isSuperadmin && (
        <div className="flex items-center gap-2" data-testid="scope-toggle">
          <button
            onClick={() => setScope('mine')}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${scope === 'mine' ? 'border-blue-500 text-blue-300' : 'border-slate-700 text-slate-400 hover:text-slate-200'}`}
            style={{ background: scope === 'mine' ? 'rgba(59, 130, 246, 0.12)' : 'transparent' }}
            data-testid="scope-mine-btn"
          >
            <UserIcon className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" />
            Mis revisiones
          </button>
          <button
            onClick={() => setScope('all')}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${scope === 'all' ? 'border-purple-500 text-purple-300' : 'border-slate-700 text-slate-400 hover:text-slate-200'}`}
            style={{ background: scope === 'all' ? 'rgba(168, 85, 247, 0.12)' : 'transparent' }}
            data-testid="scope-all-btn"
          >
            <Shield className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" />
            Todas las revisiones
          </button>
          {isAllScope && (
            <span className="text-xs italic" style={{ color: 'var(--color-text-muted)' }}>
              Vista de supervisión · sin efectos sobre destinatarios
            </span>
          )}
        </div>
      )}

      {/* Filter bar */}
      <div
        className="rounded-xl p-4"
        style={{ background: 'var(--color-bg-secondary)', border: '1px solid var(--color-border)' }}
        data-testid="filter-bar"
      >
        <div className="flex items-center gap-2 mb-3 text-sm font-medium" style={{ color: 'var(--color-text-secondary)' }}>
          <Filter className="w-4 h-4" />
          Filtros
          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="ml-auto text-xs flex items-center gap-1 px-2 py-1 rounded hover:bg-white/5"
              style={{ color: 'var(--color-text-muted)' }}
              data-testid="clear-filters-btn"
            >
              <XIcon className="w-3 h-3" /> Limpiar
            </button>
          )}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {/* Estado */}
          <div>
            <label className="text-[11px] uppercase tracking-wide" style={{ color: 'var(--color-text-muted)' }}>Estado</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full mt-1 px-2 py-1.5 rounded text-sm bg-slate-900/40 border border-slate-700 text-slate-200"
              data-testid="filter-status"
            >
              <option value="pendiente">Pendiente</option>
              <option value="pospuesto">Pospuesto</option>
              <option value="revisado">Resuelto</option>
              <option value="all">Todos</option>
            </select>
          </div>
          {/* Prioridad */}
          <div>
            <label className="text-[11px] uppercase tracking-wide" style={{ color: 'var(--color-text-muted)' }}>Prioridad</label>
            <select
              value={priorityFilter}
              onChange={(e) => setPriorityFilter(e.target.value)}
              className="w-full mt-1 px-2 py-1.5 rounded text-sm bg-slate-900/40 border border-slate-700 text-slate-200"
              data-testid="filter-priority"
            >
              <option value="">Cualquiera</option>
              <option value="alta">Alta</option>
              <option value="media">Media</option>
              <option value="baja">Baja</option>
            </select>
          </div>
          {!isAllScope && (
            <>
              <div>
                <label className="text-[11px] uppercase tracking-wide" style={{ color: 'var(--color-text-muted)' }}>Fecha límite</label>
                <select
                  value={dueFilter}
                  onChange={(e) => setDueFilter(e.target.value)}
                  className="w-full mt-1 px-2 py-1.5 rounded text-sm bg-slate-900/40 border border-slate-700 text-slate-200"
                  data-testid="filter-due"
                >
                  <option value="">Cualquier</option>
                  <option value="vencido">Vencidos</option>
                  <option value="proximos7">Próximos 7 días</option>
                  <option value="sin_fecha">Sin fecha</option>
                </select>
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-wide" style={{ color: 'var(--color-text-muted)' }}>Ordenar</label>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="w-full mt-1 px-2 py-1.5 rounded text-sm bg-slate-900/40 border border-slate-700 text-slate-200"
                  data-testid="filter-sort"
                >
                  <option value="due_asc">Vencimiento: más próximo primero</option>
                  <option value="prioridad">Prioridad alta primero</option>
                  <option value="fecha_desc">Más recientes</option>
                  <option value="fecha_asc">Más antiguos</option>
                </select>
              </div>
            </>
          )}
          {isAllScope && (
            <>
              <div>
                <label className="text-[11px] uppercase tracking-wide" style={{ color: 'var(--color-text-muted)' }}>Destinatario</label>
                <select
                  value={destinatarioFilter}
                  onChange={(e) => setDestinatarioFilter(e.target.value)}
                  className="w-full mt-1 px-2 py-1.5 rounded text-sm bg-slate-900/40 border border-slate-700 text-slate-200"
                  data-testid="filter-destinatario"
                >
                  <option value="">Todos</option>
                  {supUsers.map(u => (
                    <option key={u.id} value={u.id}>{u.name} · {ROLE_LABELS[u.role] || u.role || '—'}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-wide" style={{ color: 'var(--color-text-muted)' }}>Solicitante</label>
                <select
                  value={solicitanteFilter}
                  onChange={(e) => setSolicitanteFilter(e.target.value)}
                  className="w-full mt-1 px-2 py-1.5 rounded text-sm bg-slate-900/40 border border-slate-700 text-slate-200"
                  data-testid="filter-solicitante"
                >
                  <option value="">Todos</option>
                  {supUsers.map(u => (
                    <option key={u.id} value={u.id}>{u.name} · {ROLE_LABELS[u.role] || u.role || '—'}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-wide" style={{ color: 'var(--color-text-muted)' }}>Vencimiento</label>
                <select
                  value={vencimientoFilter}
                  onChange={(e) => setVencimientoFilter(e.target.value)}
                  className="w-full mt-1 px-2 py-1.5 rounded text-sm bg-slate-900/40 border border-slate-700 text-slate-200"
                  data-testid="filter-vencimiento"
                >
                  <option value="todas">Todas</option>
                  <option value="vencidas">Vencidas</option>
                  <option value="en_plazo">En plazo</option>
                </select>
              </div>
            </>
          )}
          {/* Búsqueda por activo — común */}
          <div className="col-span-2">
            <label htmlFor="review-asset-search" data-testid="filter-asset-search-label" className="text-[11px] uppercase tracking-wide" style={{ color: 'var(--color-text-muted)' }}>Activo (NPL / dirección / {canSearchSupplierId ? 'ID proveedor / ' : ''}ID préstamo)</label>
            <input
              id="review-asset-search"
              maxLength={300}
              value={assetSearch}
              onChange={(e) => setAssetSearch(e.target.value)}
              placeholder="NPL, dirección o identificador"
              className="w-full mt-1 px-2 py-1.5 rounded text-sm bg-slate-900/40 border border-slate-700 text-slate-200 placeholder-slate-500"
              data-testid="filter-asset-search"
            />
          </div>
        </div>
        {!isAllScope && (
          <div className="mt-3 flex items-center">
            <label className="inline-flex items-center gap-2 text-sm cursor-pointer" style={{ color: 'var(--color-text-secondary)' }}>
              <input
                type="checkbox"
                checked={assignedToMe}
                onChange={(e) => setAssignedToMe(e.target.checked)}
                className="cursor-pointer"
                data-testid="filter-assigned-to-me"
              />
              Solo cambios hechos por otros (excluye mis propias acciones)
            </label>
          </div>
        )}
      </div>

      {/* List */}
      <div
        className="rounded-xl"
        style={{ background: 'var(--color-bg-secondary)', border: '1px solid var(--color-border)' }}
        data-testid="pendientes-list"
      >
        {loading ? (
          <div className="p-10 text-center text-sm" data-testid="pendientes-loading" style={{ color: 'var(--color-text-muted)' }}>Cargando…</div>
        ) : error ? (
          <div className="p-6 text-sm text-red-400" role="alert" data-testid="pendientes-error">{error}</div>
        ) : filteredItems.length === 0 ? (
          <div className="p-10 text-center" data-testid="pendientes-empty">
            <ClipboardList className="w-10 h-10 mx-auto mb-3" style={{ color: 'var(--color-text-muted)' }} />
            <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
              {statusFilter === 'pendiente'
                ? (isAllScope ? 'No hay revisiones pendientes en la aplicación.' : 'No tienes pendientes abiertos. ¡Buen trabajo!')
                : 'No se han encontrado pendientes con los filtros aplicados.'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="pending-reviews-table text-sm">
              <thead>
                <tr style={{ borderBottom: '1px solid var(--color-border)', color: 'var(--color-text-muted)' }} className="text-[11px] uppercase tracking-wide">
                  <th className="text-left px-4 py-3 font-medium">Prioridad</th>
                  <th className="text-left px-4 py-3 font-medium w-56">Activo</th>
                  <th className="text-left px-4 py-3 font-medium">Tipo operativo</th>
                  {canSearchSupplierId && <th className="text-left px-4 py-3 font-medium">ID Proveedor</th>}
                  {isAllScope ? (
                    <>
                      <th className="text-left px-4 py-3 font-medium">Solicitante</th>
                      <th className="text-left px-4 py-3 font-medium">Destinatario</th>
                    </>
                  ) : (
                    <>
                      <th className="text-left px-4 py-3 font-medium">Usuario solicitante</th>
                      <th className="text-left px-4 py-3 font-medium">Cambio</th>
                    </>
                  )}
                  <th className="text-left px-4 py-3 font-medium">F. solicitud</th>
                  <th className="text-left px-4 py-3 font-medium">F. límite</th>
                  {isAllScope && <th className="text-left px-4 py-3 font-medium">Días</th>}
                  <th className="text-left px-4 py-3 font-medium">Estado</th>
                  {!isAllScope && <th className="text-right px-4 py-3 font-medium">Acciones</th>}
                </tr>
              </thead>
              <tbody>
                {filteredItems.map((it) => {
                  const pc = PRIORITY_COLORS[it.priority] || PRIORITY_COLORS.media;
                  const isRevisado = it.status === 'revisado';
                  const isVencida = !!it.vencida;
                  const label = ACTION_LABELS[it.action] || it.action;
                  const rowTestId = isAllScope ? `${it.audit_log_id}-${it.destinatario_id || 'me'}` : it.audit_log_id;
                  return (
                    <tr
                      key={`${it.audit_log_id}-${it.destinatario_id || 'me'}`}
                      className="border-b border-slate-800/60 hover:bg-white/[0.02]"
                      data-testid={`pendiente-row-${rowTestId}`}
                    >
                      {/* Prioridad */}
                      <td className="px-4 py-3" data-label="Prioridad">
                        {isAllScope ? (
                          <span
                            className="px-2 py-1 text-xs font-medium rounded"
                            style={{ background: pc.bg, color: pc.text }}
                            data-testid={`priority-badge-${rowTestId}`}
                          >
                            {pc.label}
                          </span>
                        ) : (
                          <select
                            value={it.priority}
                            disabled={isRevisado}
                            onChange={(e) => updatePriority(it.audit_log_id, e.target.value)}
                            className="px-2 py-1 text-xs font-medium rounded border border-transparent focus:border-slate-600"
                            style={{ background: pc.bg, color: pc.text }}
                            data-testid={`priority-select-${rowTestId}`}
                          >
                            <option value="alta">Alta</option>
                            <option value="media">Media</option>
                            <option value="baja">Baja</option>
                          </select>
                        )}
                      </td>
                      {/* Activo */}
                      <td className="px-4 py-3" data-label="Activo">
                        {it.asset_id ? (
                          <div>
                            <Link
                              to={`/activos/${it.asset_id}`}
                              className="font-medium hover:underline"
                              style={{ color: 'var(--color-blue-accent)' }}
                              data-testid={`asset-link-${rowTestId}`}
                            >
                              {it.asset_codigo_npl || it.asset_id.slice(0, 8) + '…'}
                            </Link>
                            <p className="text-xs mt-0.5 break-words" style={{ color: 'var(--color-text-muted)' }}>
                              {it.asset_direccion || '—'}
                              {it.asset_municipio ? ` · ${it.asset_municipio}` : ''}
                            </p>
                          </div>
                        ) : (
                          <span style={{ color: 'var(--color-text-muted)' }}>—</span>
                        )}
                      </td>
                      {/* Tipo operativo — visible para todos los roles */}
                      <td className="px-4 py-3 text-xs" data-label="Tipo operativo" data-testid={`tipo-operativo-${rowTestId}`} style={{ color: 'var(--color-text-secondary)' }}>
                        {TIPO_OP_LABELS[it.asset_tipo_activo_operativo] || it.asset_tipo_activo_operativo || '—'}
                      </td>
                      {/* ID Proveedor — admin/superadmin siempre; gestor solo en activos asignados; mediador/inversor nunca (columna oculta) */}
                      {canSearchSupplierId && (
                        <td className="px-4 py-3 text-xs" data-label="ID Proveedor" data-testid={`id-proveedor-${rowTestId}`} style={{ color: 'var(--color-text-secondary)' }}>
                          {it.asset_id_proveedor || '—'}
                        </td>
                      )}
                      {/* Solicitante + Destinatario (all) o Cambio (mine) */}
                      {isAllScope ? (
                        <>
                          <td className="px-4 py-3 text-xs" data-label="Solicitante" style={{ color: 'var(--color-text-secondary)' }}>
                            {it.requester_name || '—'}
                          </td>
                          <td className="px-4 py-3 text-xs" data-label="Destinatario">
                            <div>
                            <span style={{ color: 'var(--color-text-secondary)' }}>{it.destinatario_name || '—'}</span>
                            <p className="text-[10px]" style={{ color: 'var(--color-text-muted)' }}>
                              {ROLE_LABELS[it.destinatario_role] || it.destinatario_role || ''}
                            </p>
                            </div>
                          </td>
                        </>
                      ) : (
                        <>
                        <td className="px-4 py-3 text-xs" data-label="Usuario solicitante" data-testid={`requester-name-${rowTestId}`} style={{ color: 'var(--color-text-secondary)' }}>
                          {it.requester_name || '—'}
                        </td>
                        <td className="px-4 py-3" data-label="Cambio"><div>
                          <p className="font-medium" style={{ color: 'var(--color-text-primary)' }}>{label}</p>
                          {(() => {
                            const details = describeChangeDetails(it);
                            if (details.length === 0) return null;
                            return (
                              <ul className="text-xs mt-0.5 space-y-0.5" style={{ color: 'var(--color-text-muted)' }}>
                                {details.slice(0, 3).map((d, i) => (
                                  <li key={i} className="leading-snug">{d}</li>
                                ))}
                                {details.length > 3 && (
                                  <li className="leading-snug italic">… y {details.length - 3} más</li>
                                )}
                              </ul>
                            );
                          })()}
                        </div></td>
                        </>
                      )}
                      {/* Fecha solicitud */}
                      <td className="px-4 py-3 text-xs" data-label="Solicitud" style={{ color: 'var(--color-text-secondary)' }}>
                        {fmtDate(it.created_at)}
                      </td>
                      {/* Fecha límite */}
                      <td className="px-4 py-3" data-label="Vencimiento">
                        {isAllScope ? (
                          <span
                            className={`text-xs ${isVencida ? 'text-red-400 font-medium' : 'text-slate-300'}`}
                            data-testid={`deadline-${rowTestId}`}
                          >
                            {it.due_date ? fmtDateOnly(it.due_date) : it.deadline_at ? fmtDate(it.deadline_at) : '—'}
                          </span>
                        ) : (
                          <div className="review-date-fields">
                            <input
                              type="date"
                              value={it.due_date || it.deadline_at?.slice(0, 10) || ''}
                              disabled={isRevisado || !!it.postponement}
                              aria-label="Fecha límite"
                              onChange={(e) => updateDueDate(it.audit_log_id, e.target.value)}
                              className={`px-2 py-1 text-xs rounded border border-slate-700 bg-slate-900/40 ${dueColor(it.due_date, todayIso)}`}
                              data-testid={`due-input-${rowTestId}`}
                            />
                            {it.due_date && (
                              <p className="text-[10px] mt-0.5" style={{ color: 'var(--color-text-muted)' }}>
                                {fmtDateOnly(it.due_date)}
                              </p>
                            )}
                            {it.deadline_at && (
                              <p className="text-[10px] mt-0.5" style={{ color: isVencida ? '#fca5a5' : 'var(--color-text-muted)' }}>
                                <Clock className="w-2.5 h-2.5 inline -mt-0.5 mr-0.5" />
                                Plazo: {fmtDate(it.deadline_at)}
                              </p>
                            )}
                          </div>
                        )}
                      </td>
                      {/* Días desde solicitud (solo all) */}
                      {isAllScope && (
                        <td className="px-4 py-3 text-xs" data-label="Días" style={{ color: 'var(--color-text-secondary)' }}>
                          {it.dias_desde_solicitud ?? 0} d
                        </td>
                      )}
                      {/* Estado — únicamente Pendiente, Resuelto o Pospuesto */}
                      <td className="px-4 py-3" data-label="Estado">
                        <div className="flex flex-col gap-1">
                          {isRevisado ? (
                            <span
                              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium w-fit"
                              style={{ background: 'rgba(16, 185, 129, 0.15)', color: '#6ee7b7' }}
                              data-testid={`estado-badge-${rowTestId}`}
                            >
                              <CheckCircle2 className="w-3 h-3" /> Resuelto
                            </span>
                          ) : it.is_postponed ? (
                            <span
                              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium w-fit"
                              style={{ background: 'rgba(168, 85, 247, 0.18)', color: '#d8b4fe' }}
                              data-testid={`estado-badge-${rowTestId}`}
                            >
                              <CalendarClock className="w-3 h-3" /> Pospuesto
                            </span>
                          ) : (
                            <span
                              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium w-fit"
                              style={{ background: 'rgba(251, 191, 36, 0.18)', color: '#fcd34d' }}
                              data-testid={`estado-badge-${rowTestId}`}
                            >
                              <AlertCircle className="w-3 h-3" /> Pendiente
                            </span>
                          )}
                          {isVencida && !isRevisado && (
                            <span
                              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold w-fit"
                              style={{ background: 'rgba(239, 68, 68, 0.25)', color: '#fca5a5', border: '1px solid #ef4444' }}
                              data-testid={`vencida-badge-${rowTestId}`}
                            >
                              <AlertTriangle className="w-3 h-3" /> VENCIDA
                            </span>
                          )}
                          <ReviewPostponementInfo review={it} prefix={`review-${rowTestId}`} showBadge={false} />
                        </div>
                      </td>
                      {/* Acciones — no en scope=all (supervisión) */}
                      {!isAllScope && (
                        <td className="px-4 py-3 text-right" data-label="Acciones">
                          <div className="review-actions inline-flex flex-wrap justify-end items-center gap-1">
                            {!isRevisado && it.can_act_on_request && (
                              <>
                                <button onClick={() => setResolveTarget(it)}
                                  className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs hover:bg-emerald-500/10"
                                  style={{ color: '#6ee7b7' }}
                                  data-testid={`btn-resolver-${rowTestId}`}><CheckCircle2 className="w-3 h-3" />Resolver</button>
                                <button onClick={() => setPostponeTarget(it)}
                                  className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs hover:bg-white/5"
                                  data-testid={`btn-posponer-${rowTestId}`}><CalendarClock className="w-3 h-3" />Posponer</button>
                              </>
                            )}
                            {it.asset_id && (
                              <Link
                                to={`/activos/${it.asset_id}`}
                                className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs hover:bg-white/5"
                                style={{ color: 'var(--color-blue-accent)' }}
                                data-testid={`btn-ver-activo-${rowTestId}`}
                              >
                                <ExternalLink className="w-3 h-3" /> Ver activo
                              </Link>
                            )}
                            {!isInversor && (
                              <button
                                onClick={() => handleCrearTarea(it)}
                                className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs opacity-60 hover:opacity-100 hover:bg-white/5"
                                title="Funcionalidad preparada para el módulo Tareas"
                                style={{ color: 'var(--color-text-muted)' }}
                                data-testid={`btn-crear-tarea-${rowTestId}`}
                              >
                                <ListPlus className="w-3 h-3" /> Crear tarea
                              </button>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <p className="text-xs text-center" data-testid="pendientes-result-count" style={{ color: 'var(--color-text-muted)' }}>
        Mostrando {filteredItems.length} de {total} resultados con los filtros actuales.
      </p>
      {postponeTarget && <ReviewPostponeDialog item={postponeTarget} endpoint={`/assets/${postponeTarget.asset_id}/review-request/posponer`}
        onCancel={() => setPostponeTarget(null)}
        onSaved={async () => { setPostponeTarget(null); await fetchItems(); toast.success(<span data-testid="review-postpone-success">Solicitud pospuesta para todos los destinatarios</span>); }} />}
      <ResolveReviewModal
        open={!!resolveTarget}
        onClose={() => setResolveTarget(null)}
        asset={resolveTarget ? { id: resolveTarget.asset_id, marked_for_review_by_name: resolveTarget.user_name } : null}
        onSuccess={async () => { setResolveTarget(null); await fetchItems(); }}
      />
    </div>
  );
};

export default PendientesRevisionPage;
